import React, { useState } from 'react';
import styled from 'styled-components';
import { ProgressBar, Tabs } from 'antd-mobile';
import { PlayOutline, ClockCircleOutline, StarOutline, UserOutline, CheckCircleOutline } from 'antd-mobile-icons';
import { useParams, useNavigate } from 'react-router-dom';
import TextLightScroll from '../../components/texts/TextLightScroll';

const OnlineCourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // 模拟课程数据
  const courseData = {
    '1': {
      title: '量化交易入门与实战',
      description: '从零开始学习量化交易，掌握Python编程、数据分析、策略开发等核心技能。本课程将带您深入了解量化交易的核心概念，学习如何使用Python进行金融数据分析，构建交易策略，并进行回测验证。',
      instructor: {
        name: '张教授',
        title: '金融工程博士，资深量化分析师',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop',
        experience: '10年量化交易经验，曾任职于知名投资银行'
      },
      duration: '8小时',
      level: '初级',
      rating: 4.8,
      students: 1250,
      price: 299,
      originalPrice: 399,
      thumbnail: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=600&h=400&fit=crop',
      tags: ['Python', '金融', '数据分析', '量化策略'],
      chapters: [
        {
          id: 1,
          title: '量化交易基础概念',
          duration: '45分钟',
          completed: true,
          lessons: [
            { id: 1, title: '什么是量化交易', duration: '15分钟', type: 'video' },
            { id: 2, title: '量化交易的优势', duration: '12分钟', type: 'video' },
            { id: 3, title: '常见的量化策略类型', duration: '18分钟', type: 'video' }
          ]
        },
        {
          id: 2,
          title: 'Python金融数据处理',
          duration: '90分钟',
          completed: false,
          lessons: [
            { id: 4, title: '金融数据获取', duration: '25分钟', type: 'video' },
            { id: 5, title: '数据清洗与预处理', duration: '30分钟', type: 'video' },
            { id: 6, title: '技术指标计算', duration: '35分钟', type: 'video' }
          ]
        },
        {
          id: 3,
          title: '策略开发与回测',
          duration: '120分钟',
          completed: false,
          lessons: [
            { id: 7, title: '移动平均策略', duration: '40分钟', type: 'video' },
            { id: 8, title: '均值回归策略', duration: '45分钟', type: 'video' },
            { id: 9, title: '回测框架搭建', duration: '35分钟', type: 'video' }
          ]
        },
        {
          id: 4,
          title: '风险管理与实盘交易',
          duration: '75分钟',
          completed: false,
          lessons: [
            { id: 10, title: '风险控制机制', duration: '25分钟', type: 'video' },
            { id: 11, title: '资金管理策略', duration: '30分钟', type: 'video' },
            { id: 12, title: '实盘交易注意事项', duration: '20分钟', type: 'video' }
          ]
        }
      ],
      requirements: [
        '具备基础的Python编程能力',
        '了解基本的金融市场概念',
        '有一定的数学统计基础'
      ],
      whatYouWillLearn: [
        '掌握量化交易的核心理念和方法',
        '学会使用Python进行金融数据分析',
        '能够独立开发和测试交易策略',
        '了解风险管理和资金管理的重要性',
        '具备实盘交易的基础知识'
      ]
    }
  };

  const course = courseData[id as keyof typeof courseData];

  if (!course) {
    return (
      <StyledWrapper>
        <div className="error-container">
          <h2>课程未找到</h2>
          <button className="back-btn" onClick={() => navigate('/learn/courses')}>返回课程列表</button>
        </div>
      </StyledWrapper>
    );
  }

  const completedLessons = course.chapters.reduce((acc, chapter) => {
    return acc + chapter.lessons.filter(lesson => chapter.completed).length;
  }, 0);
  const totalLessons = course.chapters.reduce((acc, chapter) => acc + chapter.lessons.length, 0);
  const progress = Math.round((completedLessons / totalLessons) * 100);

  const tabs = [
    { key: 'overview', title: '课程概述' },
    { key: 'curriculum', title: '课程大纲' },
    { key: 'instructor', title: '讲师介绍' },
    { key: 'reviews', title: '学员评价' }
  ];

  return (
    <StyledWrapper>
      <div className="course-detail-container">
        {/* 课程头部信息 */}
        <div className="course-header">
          <div className="course-info">
            <div className="breadcrumb">
              <span onClick={() => navigate('/learn/courses')}>在线课程</span>
              <span> / </span>
              <span>{course.title}</span>
            </div>
            <TextLightScroll text={course.title} />
            <p className="description">{course.description}</p>
            
            <div className="course-meta">
              <div className="meta-item">
                <StarOutline />
                <span>{course.rating} ({course.students}人学习)</span>
              </div>
              <div className="meta-item">
                <ClockCircleOutline />
                <span>{course.duration}</span>
              </div>
              <div className="meta-item">
                <UserOutline />
                <span>{course.instructor.name}</span>
              </div>
            </div>
            
            <div className="course-tags">
              {course.tags.map((tag, index) => (
                <span key={index} className="tag">{tag}</span>
              ))}
            </div>
          </div>
          
          <div className="course-thumbnail">
            <img src={course.thumbnail} alt={course.title} />
            <div className="play-overlay">
              <PlayOutline className="play-icon" />
            </div>
          </div>
        </div>

        {/* 购买/学习卡片 */}
        <div className="purchase-card">
          <div className="purchase-info">
            <div className="instructor-section">
              <img src={course.instructor.avatar} alt={course.instructor.name} className="instructor-avatar" />
              <div className="instructor-details">
                <h4>{course.instructor.name}</h4>
                <p>{course.instructor.title}</p>
              </div>
            </div>
            
            <div className="price-section">
              <div className="prices">
                <span className="current-price">¥{course.price}</span>
                <span className="original-price">¥{course.originalPrice}</span>
                <span className="discount">限时优惠</span>
              </div>
            </div>
          </div>
          
          <div className="progress-section">
            <div className="progress-info">
              <span>学习进度</span>
              <span>{progress}%</span>
            </div>
            <ProgressBar percent={progress} />
          </div>
          <button className="learn-btn">
            {progress > 0 ? '继续学习' : '开始学习'}
          </button>
        </div>

        {/* 标签页内容 */}
        <div className="tabs-container">
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            {tabs.map(tab => (
              <Tabs.Tab title={tab.title} key={tab.key}>
                <div className="tab-content">
                  {tab.key === 'overview' && (
                    <div className="overview-content">
                      <div className="section">
                        <h3>课程目标</h3>
                        <ul>
                          {course.whatYouWillLearn.map((item, index) => (
                            <li key={index}>
                              <CheckCircleOutline className="check-icon" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="section">
                        <h3>课程要求</h3>
                        <ul>
                          {course.requirements.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                  
                  {tab.key === 'curriculum' && (
                    <div className="curriculum-content">
                      {course.chapters.map((chapter, index) => (
                        <div key={chapter.id} className="chapter-card">
                          <div className="chapter-header">
                            <h4>第{index + 1}章：{chapter.title}</h4>
                            <div className="chapter-meta">
                              <span>{chapter.duration}</span>
                              {chapter.completed && <CheckCircleOutline className="completed-icon" />}
                            </div>
                          </div>
                          <div className="lessons-list">
                            {chapter.lessons.map((lesson) => (
                              <div key={lesson.id} className="lesson-item">
                                <PlayOutline className="lesson-icon" />
                                <span className="lesson-title">{lesson.title}</span>
                                <span className="lesson-duration">{lesson.duration}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {tab.key === 'instructor' && (
                    <div className="instructor-content">
                      <div className="instructor-card">
                        <div className="instructor-info">
                          <img src={course.instructor.avatar} alt={course.instructor.name} className="instructor-avatar" />
                          <div className="instructor-details">
                            <h3>{course.instructor.name}</h3>
                            <p className="instructor-title">{course.instructor.title}</p>
                            <p className="instructor-experience">{course.instructor.experience}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {tab.key === 'reviews' && (
                    <div className="reviews-content">
                      <div className="reviews-summary">
                        <div className="rating-overview">
                          <div className="rating-score">{course.rating}</div>
                          <div className="rating-stars">★★★★★</div>
                          <div className="rating-count">{course.students}人评价</div>
                        </div>
                      </div>
                      
                      <div className="reviews-list">
                        <div className="review-item">
                          <div className="review-header">
                            <div className="reviewer-name">学员A</div>
                            <div className="review-rating">★★★★★</div>
                          </div>
                          <p>课程内容非常实用，老师讲解清晰，从基础概念到实际应用都有涉及，对量化交易有了全面的了解。</p>
                        </div>
                        
                        <div className="review-item">
                          <div className="review-header">
                            <div className="reviewer-name">学员B</div>
                            <div className="review-rating">★★★★☆</div>
                          </div>
                          <p>Python代码示例很丰富，跟着练习能够快速上手。建议增加更多实际案例分析。</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Tabs.Tab>
            ))}
          </Tabs>
        </div>
      </div>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  .course-detail-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 40px 20px;
    color: #333;
  }

  .error-container {
    text-align: center;
    padding: 100px 20px;

    h2 {
      margin-bottom: 20px;
      color: #333;
    }

    .back-btn {
      background: linear-gradient(135deg, #4CAF50, #45a049);
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 8px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
      }
    }
  }

  .course-header {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 40px;
    margin-bottom: 30px;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 20px;
    }
  }

  .breadcrumb {
    font-size: 0.9rem;
    opacity: 0.8;
    margin-bottom: 10px;
    
    span:first-child {
      cursor: pointer;
      text-decoration: underline;
    }
  }

  .course-info {
    h1 {
      font-size: 2.2rem;
      font-weight: bold;
      margin: 0 0 15px 0;
      color: #333;
      line-height: 1.3;
    }

    .description {
      font-size: 1.1rem;
      line-height: 1.6;
      color: #666;
      margin-bottom: 20px;
    }
  }

  .course-meta {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    flex-wrap: wrap;

    .meta-item {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 0.95rem;
      color: #666;
    }
  }

  .course-tags {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;

    .tag {
      background: #f0f2f5;
      color: #333;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 0.85rem;
      border: 1px solid #e0e0e0;
    }
  }

  .course-thumbnail {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    height: fit-content;
    
    img {
      width: 100%;
      height: auto;
      display: block;
    }
    
    .play-overlay {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.7);
      border-radius: 50%;
      width: 60px;
      height: 60px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        background: rgba(0, 0, 0, 0.8);
        transform: translate(-50%, -50%) scale(1.1);
      }
    }
    
    .play-icon {
      color: white;
      font-size: 24px;
    }
  }

  .purchase-card {
    background: white;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    border: 1px solid #e0e0e0;
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 30px;
  }

  .purchase-info {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 15px;
    }
  }

  .instructor-section {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .instructor-avatar {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      object-fit: cover;
    }
    
    .instructor-details {
      h4 {
        margin: 0 0 4px 0;
        color: #333;
        font-size: 1rem;
      }

      p {
        margin: 0;
        font-size: 0.85rem;
        color: #666;
      }
    }
  }

  .price-section {
    text-align: right;
    
    @media (max-width: 768px) {
      text-align: left;
    }
    
    .prices {
      display: flex;
      align-items: center;
      gap: 10px;
      
      @media (max-width: 768px) {
        justify-content: flex-start;
      }
    }
    
    .current-price {
      font-size: 1.8rem;
      font-weight: bold;
      color: #FFD700;
    }
    
    .original-price {
      font-size: 1rem;
      text-decoration: line-through;
      opacity: 0.6;
    }
    
    .discount {
      background: #FF4757;
      color: white;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 0.75rem;
    }
  }

  .learn-btn {
    width: 100%;
    padding: 12px;
    background: linear-gradient(135deg, #4CAF50, #45a049);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
    }
  }

  .progress-section {
    margin-bottom: 20px;
    
    .progress-info {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
      font-size: 0.9rem;
    }
  }

  .tabs-container {
    background: white;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    border: 1px solid #e0e0e0;
    border-radius: 16px;
    padding: 0;
    overflow: hidden;
  }

  .tab-content {
    padding: 24px;
  }

  .overview-content {
    .section {
      margin-bottom: 30px;

      h3 {
        font-size: 1.3rem;
        margin-bottom: 15px;
        color: #333;
      }
      
      ul {
        list-style: none;
        padding: 0;
        
        li {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin-bottom: 10px;
          line-height: 1.5;
          
          .check-icon {
            color: #4CAF50;
            margin-top: 2px;
            flex-shrink: 0;
          }
        }
      }
    }
  }

  .curriculum-content {
    .chapter-card {
      background: #f8f9fa;
      border: 1px solid #e0e0e0;
      border-radius: 12px;
      margin-bottom: 20px;
      padding: 20px;
    }

    .chapter-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;

      h4 {
        margin: 0;
        color: #333;
        font-size: 1.1rem;
      }
      
      .chapter-meta {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 0.9rem;
        color: #666;

        .completed-icon {
          color: #4CAF50;
        }
      }
    }

    .lessons-list {
      .lesson-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 0;
        border-bottom: 1px solid #e0e0e0;

        &:last-child {
          border-bottom: none;
        }

        .lesson-icon {
          color: #4CAF50;
          font-size: 14px;
        }

        .lesson-title {
          flex: 1;
          font-size: 0.9rem;
          color: #333;
        }

        .lesson-duration {
          font-size: 0.8rem;
          color: #666;
        }
      }
    }
  }

  .instructor-content {
    .instructor-card {
      background: #f8f9fa;
      border: 1px solid #e0e0e0;
      border-radius: 12px;
      padding: 24px;
    }

    .instructor-info {
      display: flex;
      gap: 20px;
      align-items: flex-start;

      .instructor-avatar {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        object-fit: cover;
      }

      .instructor-details {
        flex: 1;

        h3 {
          margin: 0 0 8px 0;
          color: #333;
          font-size: 1.3rem;
        }

        .instructor-title {
          font-size: 1rem;
          color: #4CAF50;
          margin: 0 0 10px 0;
        }

        .instructor-experience {
          font-size: 0.95rem;
          line-height: 1.5;
          color: #666;
          margin: 0;
        }
      }
    }
  }

  .reviews-content {
    .reviews-summary {
      margin-bottom: 30px;
      
      .rating-overview {
        text-align: center;
        
        .rating-score {
          font-size: 3rem;
          font-weight: bold;
          color: #FFD700;
        }
        
        .rating-stars {
          font-size: 1.5rem;
          color: #FFD700;
          margin: 10px 0;
        }
        
        .rating-count {
          font-size: 0.9rem;
          opacity: 0.8;
        }
      }
    }
    
    .reviews-list {
      .review-item {
        background: #f8f9fa;
        border: 1px solid #e0e0e0;
        border-radius: 12px;
        padding: 20px;
        margin-bottom: 15px;

        .review-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;

          .reviewer-name {
            font-weight: 600;
            color: #333;
          }

          .review-rating {
            color: #FFD700;
          }
        }

        p {
          margin: 0;
          line-height: 1.5;
          color: #666;
        }
      }
    }
  }
`;

export default OnlineCourseDetail;